import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Image, Folder, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { MobileNavigation } from "@/components/MobileNavigation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState, useEffect } from "react";

const Dashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [portfolioToDelete, setPortfolioToDelete] = useState<{ id: string; title: string } | null>(null);

  // First, check the user's role
  const { data: userRole, isLoading: roleLoading } = useQuery({
    queryKey: ["userRole"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return null;
      }

      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      if (roleError) {
        console.error('Error fetching user role:', roleError);
        return null;
      }

      return roleData?.role;
    },
  });

  // Fetch portfolios only if user is a talent
  const { data: portfolios, isLoading: portfoliosLoading } = useQuery({
    queryKey: ["portfolios"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { data, error } = await supabase
        .from("portfolios")
        .select(`
          *,
          portfolio_items (*)
        `)
        .eq('user_id', user.id);

      if (error) {
        toast({
          title: "Error",
          description: "Failed to load portfolios",
          variant: "destructive",
        });
        throw error;
      }

      return data;
    },
    enabled: userRole === 'talent', // Only fetch if user is talent
  });

  // Redirect casting directors to opportunities page
  useEffect(() => {
    if (!roleLoading && userRole === 'recruiter') {
      navigate('/opportunities');
    }
  }, [userRole, roleLoading, navigate]);

  if (roleLoading || portfoliosLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If user is not a talent, don't show the portfolio management interface
  if (userRole !== 'talent') {
    return null;
  }

  const createNewPortfolio = async () => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) {
      toast({
        title: "Error",
        description: "You must be logged in to create a portfolio",
        variant: "destructive",
      });
      return;
    }

    const { data, error } = await supabase
      .from("portfolios")
      .insert([
        {
          user_id: userData.user.id,
          title: "New Portfolio",
          description: "My new portfolio",
        },
      ])
      .select()
      .single();

    if (error) {
      toast({
        title: "Error",
        description: "Failed to create portfolio",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Portfolio created successfully",
    });

    queryClient.invalidateQueries({ queryKey: ["portfolios"] });
  };

  const handleDeletePortfolio = async () => {
    if (!portfolioToDelete) return;

    const { error } = await supabase
      .from("portfolios")
      .delete()
      .eq("id", portfolioToDelete.id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete portfolio",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Portfolio deleted successfully",
    });

    setPortfolioToDelete(null);
    queryClient.invalidateQueries({ queryKey: ["portfolios"] });
  };

  return (
    <>
      <div className="container mx-auto py-8 pb-20 md:pb-8">
        <div className="flex flex-col gap-1 mb-8">
          <h1 className="text-4xl font-bold">My Portfolio</h1>
          <p className="text-muted-foreground">Projects</p>
        </div>
        <div className="flex justify-between items-center mb-8">
          <div /> {/* Empty div for spacing */}
          <Button onClick={createNewPortfolio}>
            <Plus className="mr-2 h-4 w-4" /> Add Project
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolios?.map((portfolio) => (
            <Card key={portfolio.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center">
                  <Folder className="mr-2 h-5 w-5 text-primary" />
                  <div>
                    <CardTitle>{portfolio.title}</CardTitle>
                    <CardDescription>{portfolio.description}</CardDescription>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPortfolioToDelete({ id: portfolio.id, title: portfolio.title })}
                  className="h-8 w-8"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {portfolio.portfolio_items?.length || 0} items
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/portfolio/${portfolio.id}`)}
                  >
                    <Image className="mr-2 h-4 w-4" /> View
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <AlertDialog open={!!portfolioToDelete} onOpenChange={() => setPortfolioToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to delete "{portfolioToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePortfolio} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Yes, delete project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MobileNavigation />
    </>
  );
};

export default Dashboard;